/**
 * What changed in a deploy (T-16): the build writes /changes.json from main's merged pull requests
 * (frontend/release-notes.build.ts), and the update banner's "What's new" lists the ones newer than the
 * running app.
 */

export interface ReleaseChange {
  /** The merge commit on main. */
  sha: string;
  /** When it reached main (the merge commit's committer date, ISO). */
  merged_at: string;
  /** The pull request's number, or null for a commit pushed straight to main. */
  pr: number | null;
  /** The pull request's title (or the commit's subject). */
  title: string;
  /** The PR's branch, e.g. task/T-16-what-s-new. */
  branch: string | null;
  /** The PR description's Summary bullets (plain text), when the build could read them from GitHub. */
  summary: string[];
  /** Task numbers it names, e.g. ["T-16"]. */
  tasks: string[];
  /** Docs it changed (repo paths: docs/…, README.md, AGENTS.md). */
  docs: string[];
}

export interface ReleaseNotes {
  /** owner/name on GitHub, for PR and doc links. */
  repo: string;
  /** The build's id (version.json's buildId). */
  buildId: string;
  /** Newest first. */
  changes: ReleaseChange[];
}

export const RELEASE_NOTES_PATH = '/changes.json';

/** "Merge pull request #13 from StakaraLabs/task/T-4-foo" → { pr: 13, branch: "task/T-4-foo" }. */
export function parseMergeSubject(subject: string): { pr: number; branch: string } | null {
  const match = /^Merge pull request #(\d+) from [^/\s]+\/(\S+)/.exec(subject.trim());
  return match ? { pr: Number(match[1]), branch: match[2] } : null;
}

/** Task numbers in a branch name and title ("task/T-16-…", "T-9: …", "(T-12)"), in order, once each. */
export function taskRefs(...texts: Array<string | null | undefined>): string[] {
  const refs: string[] = [];
  for (const text of texts) {
    for (const match of (text ?? '').matchAll(/(?:^|[^A-Za-z0-9])T-(\d{1,9})(?![0-9])/gi)) {
      const ref = `T-${Number(match[1])}`;
      if (!refs.includes(ref)) refs.push(ref);
    }
  }
  return refs;
}

const MAX_DOCS = 8;

/** Changed paths that are docs: Markdown under docs/ and at the repo's root, the first few. */
export function docPaths(paths: string[]): string[] {
  return paths.filter((p) => p.endsWith('.md') && (p.startsWith('docs/') || !p.includes('/'))).slice(0, MAX_DOCS);
}

const MAX_SUMMARY_LINES = 4;
const MAX_LINE_LENGTH = 240;

/** Markdown inline marks taken out, for plain text: **bold**, _em_, `code` and [text](url) → text. */
function plainText(line: string): string {
  return line
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * A PR description's summary, as plain lines: the bullets (or paragraph) under its "Summary" heading,
 * or else everything before its first heading. Factory and attribution lines are left out.
 */
export function summaryFromPrBody(body: string | null | undefined): string[] {
  if (!body) return [];
  const lines = body.replace(/\r\n?/g, '\n').split('\n');
  const headingAt = (i: number) => /^#{1,6}\s/.test(lines[i]);
  let start = lines.findIndex((l) => /^#{1,6}\s+(summary|what changed|changes)\b/i.test(l));
  if (start >= 0) start += 1;
  else start = 0;
  let end = start;
  while (end < lines.length && !headingAt(end)) end += 1;

  const out: string[] = [];
  let paragraph = '';
  const flush = () => {
    if (paragraph) out.push(paragraph);
    paragraph = '';
  };
  for (const raw of lines.slice(start, end)) {
    const line = raw.trim();
    if (!line || /^(factory:|🤖|co-authored-by:|<!--)/i.test(line) || /^[-*_]{3,}$/.test(line)) {
      flush();
      continue;
    }
    const bullet = /^(?:[-*+]|\d+[.)])\s+(?:\[[ xX]\]\s+)?(.*)$/.exec(line);
    if (bullet) {
      flush();
      paragraph = plainText(bullet[1]);
    } else {
      paragraph = paragraph ? `${paragraph} ${plainText(line)}` : plainText(line);
    }
  }
  flush();
  return out
    .filter(Boolean)
    .slice(0, MAX_SUMMARY_LINES)
    .map((l) => (l.length > MAX_LINE_LENGTH ? `${l.slice(0, MAX_LINE_LENGTH - 1).trimEnd()}…` : l));
}

/**
 * The changes newer than the running app: the ones that reached main after the commit it was built
 * from. With no date (a local build, or an app from before T-16), every change listed.
 */
export function changesSince(changes: ReleaseChange[], builtFromAt: string | null): ReleaseChange[] {
  const since = builtFromAt ? Date.parse(builtFromAt) : NaN;
  if (Number.isNaN(since)) return changes;
  return changes.filter((c) => Date.parse(c.merged_at) > since);
}

/**
 * Whether the list may not reach back to the running app: every change in it is newer than the app's
 * build (a shallow history lists only the latest), or the app's build date is unknown.
 */
export function mayBeIncomplete(changes: ReleaseChange[], builtFromAt: string | null): boolean {
  const since = builtFromAt ? Date.parse(builtFromAt) : NaN;
  if (Number.isNaN(since)) return true;
  const oldest = changes[changes.length - 1];
  return !oldest || Date.parse(oldest.merged_at) > since;
}

/** Reads /changes.json's body; anything malformed is dropped, so an old or broken file shows nothing. */
export function parseReleaseNotes(data: unknown): ReleaseNotes | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (typeof d.repo !== 'string' || !Array.isArray(d.changes)) return null;
  const strings = (v: unknown) => (Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []);
  const changes: ReleaseChange[] = [];
  for (const item of d.changes) {
    if (!item || typeof item !== 'object') continue;
    const c = item as Record<string, unknown>;
    if (typeof c.sha !== 'string' || typeof c.title !== 'string' || typeof c.merged_at !== 'string') continue;
    changes.push({
      sha: c.sha,
      merged_at: c.merged_at,
      pr: typeof c.pr === 'number' ? c.pr : null,
      title: c.title,
      branch: typeof c.branch === 'string' ? c.branch : null,
      summary: strings(c.summary),
      tasks: strings(c.tasks),
      docs: strings(c.docs),
    });
  }
  return { repo: d.repo, buildId: typeof d.buildId === 'string' ? d.buildId : '', changes };
}
